import { useState, useEffect, useRef } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { getMetaPages } from '@/services/api';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';

interface Props {
    selectedPageIds: string[];
    onChange: (pageIds: string[]) => void;
    disabled?: boolean;
}

export default function PageMultiSelect({ selectedPageIds, onChange, disabled }: Props) {
    const [pages, setPages] = useState<MetaPage[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategories, setActiveCategories] = useState<Set<number>>(new Set());

    // refs para evitar stale closure no handler de categorias
    const pagesRef = useRef<MetaPage[]>([]);
    const selectedRef = useRef<string[]>(selectedPageIds);
    const activeCatsRef = useRef<Set<number>>(new Set());

    useEffect(() => { selectedRef.current = selectedPageIds; }, [selectedPageIds]);
    useEffect(() => { activeCatsRef.current = activeCategories; }, [activeCategories]);

    useEffect(() => {
        getMetaPages()
            .then(data => {
                const active = data.filter(p => p.is_active);
                setPages(active);
                pagesRef.current = active;
            })
            .finally(() => setLoading(false));
    }, []);

    const normalize = (str: string) =>
        str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    const categories = Array.from(new Map(
        pages
            .filter(p => p.internal_category_id != null && p.internal_category_name)
            .map(p => [Number(p.internal_category_id), p.internal_category_name as string])
    ).entries()).map(([id, name]) => ({ id, name }));

    const filteredPages = pages.filter(page => {
        const matchesCat =
            activeCategories.size === 0 ||
            (page.internal_category_id != null && activeCategories.has(Number(page.internal_category_id)));
        if (!matchesCat) return false;
        if (!searchTerm.trim()) return true;
        const term = normalize(searchTerm);
        return normalize(page.page_name).includes(term) || page.page_id.includes(searchTerm.trim());
    });

    const isSelected = (pageId: string) => selectedPageIds.includes(pageId);

    const toggle = (page: MetaPage) => {
        onChange(
            isSelected(page.page_id)
                ? selectedPageIds.filter(id => id !== page.page_id)
                : [...selectedPageIds, page.page_id]
        );
    };

    const allFilteredSelected =
        filteredPages.length > 0 && filteredPages.every(p => selectedPageIds.includes(p.page_id));

    const handleToggleAll = () => {
        if (allFilteredSelected) {
            const filteredIds = new Set(filteredPages.map(p => p.page_id));
            onChange(selectedPageIds.filter(id => !filteredIds.has(id)));
        } else {
            const current = new Set(selectedPageIds);
            const toAdd = filteredPages.filter(p => !current.has(p.page_id)).map(p => p.page_id);
            onChange([...selectedPageIds, ...toAdd]);
        }
    };

    const handleCategoryChange = (vals: string[]) => {
        const newCatIds = new Set(vals.map(Number));
        const prevCatIds = activeCatsRef.current;
        const allPages = pagesRef.current;
        const currentSelected = selectedRef.current;

        // Todas as categorias já tocadas (antes + agora) — para saber o que foi "gerenciado"
        const everManagedCatIds = new Set([...Array.from(prevCatIds), ...Array.from(newCatIds)]);

        // Páginas que pertencem a alguma categoria já gerenciada
        const managedPageIds = new Set(
            allPages
                .filter(p => p.internal_category_id != null && everManagedCatIds.has(Number(p.internal_category_id)))
                .map(p => p.page_id)
        );

        // Preserva seleções manuais (páginas fora de qualquer categoria gerenciada)
        const manualIds = currentSelected.filter(id => !managedPageIds.has(id));

        // Adiciona todas as páginas das categorias agora ativas
        const fromCategories = allPages
            .filter(p => p.internal_category_id != null && newCatIds.has(Number(p.internal_category_id)))
            .map(p => p.page_id);

        setActiveCategories(newCatIds);
        onChange([...manualIds, ...fromCategories]);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {selectedPageIds.length > 0 && (
                <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-800 px-3 py-1.5 rounded-full text-sm font-medium">
                    <Check className="h-4 w-4" />
                    {selectedPageIds.length} página{selectedPageIds.length > 1 ? 's' : ''} selecionada{selectedPageIds.length > 1 ? 's' : ''}
                </div>
            )}

            <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                    <Label className="text-xs font-medium">Buscar Página</Label>
                    <Input
                        placeholder="Busque por nome ou ID..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="h-9"
                        disabled={disabled}
                    />
                </div>
                {categories.length > 0 && (
                    <div className="w-52 space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Filtrar por Categoria</Label>
                        <SearchableMultiSelect
                            options={categories.map(c => ({ value: String(c.id), label: c.name }))}
                            values={Array.from(activeCategories).map(String)}
                            onValuesChange={handleCategoryChange}
                            placeholder="Todas as categorias"
                            searchPlaceholder="Buscar categoria..."
                            showTags={false}
                            selectedLabel={n => `${n} categoria${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                        />
                    </div>
                )}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleToggleAll}
                    className="whitespace-nowrap h-9"
                    disabled={disabled || filteredPages.length === 0}
                >
                    {allFilteredSelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                </Button>
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {filteredPages.length === 0 ? (
                    <p className="text-center text-sm text-gray-500 py-6">Nenhuma página encontrada</p>
                ) : (
                    filteredPages.map(page => {
                        const selected = isSelected(page.page_id);
                        return (
                            <button
                                key={page.page_id}
                                type="button"
                                onClick={() => toggle(page)}
                                disabled={disabled}
                                className={`relative w-full p-3 border-2 rounded-xl text-left transition-all hover:border-cyan-400 hover:shadow-sm disabled:opacity-50
                                    ${selected ? 'border-cyan-400 bg-cyan-50/50 shadow-sm' : 'border-gray-200 bg-white'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                                        ${selected ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300 bg-white'}`}>
                                        {selected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                    <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-semibold text-sm">
                                            {page.page_name.substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm text-gray-900 truncate">{page.page_name}</span>
                                            {page.internal_category_name && (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase tracking-wide">
                                                    {page.internal_category_name}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500">ID: {page.page_id}</p>
                                        {page.meta_user_name && (
                                            <p className="text-xs text-gray-400">Conta: {page.meta_user_name}</p>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
